/**
 * Dashboard Service
 * 
 * Handles all dashboard-related API calls.
 */

import api from '../utils/api';

/**
 * Get dashboard overview
 * @returns {Promise} - Dashboard overview response
 */
export const getDashboardOverview = async () => {
  try {
    const response = await api.get('/v1/admin/dashboard/overview');

    if (response.status === 'success' && response.data) {
      return {
        success: true,
        data: response.data,
        message: response.message || 'Dashboard overview retrieved successfully',
      };
    }

    return {
      success: false,
      error: response.message || 'Failed to retrieve dashboard overview',
      data: null,
    };
  } catch (error) {
    console.error('Get dashboard overview error:', error);
    
    let errorMessage = 'Failed to retrieve dashboard overview. Please try again.';
    
    if (error.response) {
      const errorData = error.response;
      if (errorData.error) {
        errorMessage = errorData.error;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      }
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage,
      data: null,
    };
  }
};

/**
 * Get financial statistics
 * @returns {Promise} - Financial statistics response
 */
export const getFinancialStats = async () => {
  try {
    const response = await api.get('/v1/admin/dashboard/financial');

    if (response.status === 'success' && response.data) {
      return {
        success: true,
        data: response.data,
        message: response.message || 'Financial statistics retrieved successfully',
      };
    }

    return {
      success: false,
      error: response.message || 'Failed to retrieve financial statistics',
      data: null,
    };
  } catch (error) {
    console.error('Get financial stats error:', error);
    
    let errorMessage = 'Failed to retrieve financial statistics. Please try again.';
    
    if (error.response) {
      const errorData = error.response;
      if (errorData.error) {
        errorMessage = errorData.error;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      }
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage,
      data: null,
    };
  }
};

/**
 * Get student statistics
 * @returns {Promise} - Student statistics response
 */
export const getStudentStats = async () => {
  try {
    const response = await api.get('/v1/admin/dashboard/students');

    if (response.status === 'success' && response.data) {
      return {
        success: true,
        data: response.data,
        message: response.message || 'Student statistics retrieved successfully',
      };
    }

    return {
      success: false,
      error: response.message || 'Failed to retrieve student statistics',
      data: null,
    };
  } catch (error) {
    console.error('Get student stats error:', error);
    
    let errorMessage = 'Failed to retrieve student statistics. Please try again.';
    
    if (error.response) {
      const errorData = error.response;
      if (errorData.error) {
        errorMessage = errorData.error;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      }
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage,
      data: null,
    };
  }
};

/**
 * Get tutor statistics
 * @returns {Promise} - Tutor statistics response
 */
export const getTutorStats = async () => {
  try {
    const response = await api.get('/v1/admin/dashboard/tutors');

    if (response.status === 'success' && response.data) {
      return {
        success: true,
        data: response.data,
        message: response.message || 'Tutor statistics retrieved successfully',
      };
    }

    return {
      success: false,
      error: response.message || 'Failed to retrieve tutor statistics',
      data: null,
    };
  } catch (error) {
    console.error('Get tutor stats error:', error);
    
    let errorMessage = 'Failed to retrieve tutor statistics. Please try again.';
    
    if (error.response) {
      const errorData = error.response;
      if (errorData.error) {
        errorMessage = errorData.error;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      }
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage,
      data: null,
    };
  }
};

/**
 * Get user statistics
 * @returns {Promise} - User statistics response
 */
export const getUserStats = async () => {
  try {
    const response = await api.get('/v1/admin/dashboard/users');

    if (response.status === 'success' && response.data) {
      return {
        success: true,
        data: response.data,
        message: response.message || 'User statistics retrieved successfully',
      };
    }

    return {
      success: false,
      error: response.message || 'Failed to retrieve user statistics',
      data: null,
    };
  } catch (error) {
    console.error('Get user stats error:', error);
    
    let errorMessage = 'Failed to retrieve user statistics. Please try again.';
    
    if (error.response) {
      const errorData = error.response;
      if (errorData.error) {
        errorMessage = errorData.error;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      }
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage,
      data: null,
    };
  }
};

/**
 * Get system statistics
 * @returns {Promise} - System statistics response
 */
export const getSystemStats = async () => {
  try {
    const response = await api.get('/v1/admin/dashboard/system');

    if (response.status === 'success' && response.data) {
      return {
        success: true,
        data: response.data,
        message: response.message || 'System statistics retrieved successfully',
      };
    }

    return {
      success: false,
      error: response.message || 'Failed to retrieve system statistics',
      data: null,
    };
  } catch (error) {
    console.error('Get system stats error:', error);
    
    let errorMessage = 'Failed to retrieve system statistics. Please try again.';
    
    if (error.response) {
      const errorData = error.response;
      if (errorData.error) {
        errorMessage = errorData.error;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      }
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage,
      data: null,
    };
  }
};

const dashboardService = {
  getDashboardOverview,
  getFinancialStats,
  getStudentStats,
  getTutorStats,
  getUserStats,
  getSystemStats,
};
export default dashboardService;
